import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import type { ReactElement } from 'react';
import { CalendarOutlined, EditOutlined } from '@ant-design/icons';
import { Alert, Tag } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';

import { EntityHeader } from '../../../components/entity-detail/EntityHeader.js';
import { meetingIcsUrl, type MeetingItem } from '../api/meetings.client.js';
import { STATUS_TONE, STATUS_LABEL } from '../lib/meetingMeta.js';
import { useDeleteMeetingMutation } from '../hooks/useMeetings.js';

/**
 * Шапка карточки встречи на общем стандарте entity-detail: имя, чипы статуса и
 * места, основные действия и меню «⋯» с отменой. История переехала во вкладку,
 * поэтому отдельной кнопки-дровера здесь нет.
 */
export function MeetingHeader({
  meeting,
  onEdit,
}: {
  meeting: MeetingItem;
  onEdit: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const prefix = 'crm.features.features.meetings.pages.meetingcardpage.';
  const remove = useDeleteMeetingMutation();
  return (
    <>
      <EntityHeader
        backTo="/meetings"
        backLabel={t(`${prefix}к_встречам`)}
        title={meeting.title}
        subtitle={[meeting.location, meeting.timezone].filter(Boolean).join(' · ')}
        chips={[
          <EntityStatusChip key="status" tone={STATUS_TONE[meeting.status]}>
            {STATUS_LABEL[meeting.status]}
          </EntityStatusChip>,
          ...(meeting.location ? [<Tag key="location">{meeting.location}</Tag>] : []),
        ]}
        primary={{
          key: 'edit',
          icon: <EditOutlined aria-hidden />,
          label: t('crm.entity_detail.edit'),
          testId: 'meeting-edit',
          onClick: onEdit,
        }}
        secondary={[
          {
            key: 'ics',
            icon: <CalendarOutlined aria-hidden />,
            label: t('crm.entity_detail.download_ics'),
            href: meetingIcsUrl(meeting.id),
            testId: 'meeting-download-ics',
            onClick: () => undefined,
          },
        ]}
        overflow={[
          {
            key: 'cancel',
            label: t('crm.entity_detail.cancel_meeting'),
            danger: true,
            disabled: remove.isPending,
            confirm: {
              title: t('crm.entity_detail.cancel_meeting_confirm'),
              // Отмена — статус, а не удаление: встреча остаётся видна в списках.
              description: t(`${prefix}встреча_останется_в_списках_со_статусом_от`),
              okText: t('crm.entity_detail.cancel_meeting'),
              cancelText: t('crm.common.cancel'),
            },
            onClick: () => remove.mutateAsync(meeting.id),
          },
        ]}
      />
      {remove.error ? (
        <Alert
          showIcon
          type="error"
          title={t(`${prefix}не_удалось_отменить_встречу`)}
          description={(remove.error as Error).message}
        />
      ) : null}
    </>
  );
}
